import OpenAI from "openai";
import { stripPostPreviewHtml } from "../../helpers/postBodyHtml";

let client;
function getClient() {
  const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  if (!apiKey) {
    throw new Error(
      "Missing REACT_APP_OPENAI_API_KEY. Add it to your .env file."
    );
  }
  if (!client) {
    client = new OpenAI({
      apiKey,
      dangerouslyAllowBrowser: true,
    });
  }
  return client;
}

function unwrapModelHtml(raw) {
  let s = String(raw || "").trim();
  if (s.startsWith("```")) {
    s = s.replace(/^```[a-zA-Z]*\n?/, "").replace(/\n?```\s*$/, "");
  }
  return s.trim();
}

const SYSTEM_PROMPT = `You are an editor preparing article body content for a mobile news app.

Rules:
- Output ONLY valid HTML fragment(s). No markdown. No code fences. No preamble or explanation.
- Use only these tags: h1, h2, p, ul, ol, li, strong, em, u, a, blockquote, br.
- Use at most one h1 for the main title of the piece if the source clearly contains a title; otherwise start with h2 or p.
- If the input is JSON or mostly JSON, interpret it and turn keys into section headings (h2) and values into paragraphs or lists as appropriate. Do not output raw JSON in the result.
- If the input is messy HTML from a crawl, strip ads, navigation, scripts, styles, tracking, cookie banners, and boilerplate. Keep meaningful body content and real article links (href) where appropriate.
- Do not invent facts, quotes, dates, or names. Only reorganize and clean what is present.
- Prefer clear hierarchy: sections with h2, short paragraphs, bullet lists where it helps readability.
- Voice: Use neutral third person only. Do not write as the institution in first person (no "we", "our", "us") and do not address the reader as "you" / "your". Describe offerings impersonally ("The programme includes…", "Students may…", "The university offers…") or use concise subject-less listings where natural ("Offers postgraduate pathways…"). If the source says "we offer" or similar, rewrite to third person or neutral phrasing ("The university offers…", "The course offers…", "Offers include…").`;

/**
 * @param {string} storedOrRawBody - Full stored post body or raw HTML/text pasted in the editor
 * @param {{ extraUserInstructions?: string, contentKind?: 'article' | 'programme' }} [options]
 * @returns {Promise<string>} Inner HTML suitable for wrapPostPreviewHtml / Quill (no outer preview wrapper)
 */
export async function restructureContent(storedOrRawBody, options = {}) {
  const stripped = stripPostPreviewHtml(storedOrRawBody || "").trim();
  if (!stripped) {
    throw new Error("Nothing to restructure. Add some content first.");
  }

  const extra = (options.extraUserInstructions || "").trim();
  const kind = options.contentKind === "programme" ? "programme" : "article";
  const intro =
    kind === "programme"
      ? "Restructure the following programme or course description into clean HTML for an educational listings app (prospective students as readers). Keep third person."
      : "Restructure the following into clean article HTML:";
  let userContent = `${intro}\n\n${stripped}`;
  if (extra) {
    userContent += `\n\nAdditional instructions from the editor:\n${extra}`;
  }

  const openai = getClient();
  const completion = await openai.chat.completions.create({
    model: "gpt-4o-mini",
    temperature: 0.2,
    messages: [
      { role: "system", content: SYSTEM_PROMPT },
      {
        role: "user",
        content: userContent,
      },
    ],
  });

  const text = completion.choices[0]?.message?.content;
  if (!text) {
    throw new Error("No response from the model.");
  }
  return unwrapModelHtml(text);
}
